import { FormSection } from "$components/form-section";
import { DatePicker, RadioGroupField, TextField } from "$components/rhf-fields";
import type { Territory } from "$global-providers/config-provider/provider";
import { TERRITORY } from "$routes/_lib/constants";
import { Gender } from "elephant-web/gql";
import React from "react";
import { useIntl } from "react-intl";
export const GenderRadioInput: FC<
  {
    excludeTransgender?: boolean;
  } & Omit<React.ComponentProps<typeof RadioGroupField>, "options">
> = ({ excludeTransgender, ...delegateProps }) => {
  const { formatMessage } = useIntl();

  const options = React.useMemo(
    () =>
      [
        {
          value: Gender.M,
          label: formatMessage({
            id: "patient.generalSection.genderRadioInput.option.male",
            defaultMessage: "Male",
          }),
        },
        {
          value: Gender.F,
          label: formatMessage({
            id: "patient.generalSection.genderRadioInput.option.female",
            defaultMessage: "Female",
          }),
        },
        !excludeTransgender && {
          value: Gender.Transgender,
          label: formatMessage({
            id: "patient.generalSection.genderRadioInput.option.transgender",
            defaultMessage: "Transgender",
          }),
        },
      ].filter(Boolean),
    [formatMessage, excludeTransgender],
  );

  return (
    <RadioGroupField
      options={options}
      label={formatMessage({
        id: "patient.generalSection.genderRadioInput.label",
        defaultMessage: "Sex",
      })}
      {...delegateProps}
    />
  );
};

export const GeneralSection: React.FC<{
  territory: Territory;
  fieldNames: {
    firstName: string;
    middleName: string;
    lastName: string;
    dob: string;
    gender: string;
    newToFacility: string;
  };
  isEdit?: boolean;
}> = ({ fieldNames, territory, isEdit = false }) => {
  const { formatMessage } = useIntl();

  return (
    <FormSection
      title={formatMessage({
        id: "patient.generalSection.title",
        defaultMessage: "Patient Details",
      })}
    >
      <TextField
        fullWidth
        name={fieldNames.firstName}
        label={formatMessage({
          id: "patient.generalSection.firstName.label",
          defaultMessage: "First Name",
        })}
        placeholder="First Name"
        required
      />
      <TextField
        fullWidth
        name={fieldNames.middleName}
        label={
          territory === TERRITORY.PAKISTAN
            ? formatMessage({
                id: "patient.generalSection.middleName.label.pakistan",
                defaultMessage: "Father / Husband's Name",
              })
            : formatMessage({
                id: "patient.generalSection.middleName.label",
                defaultMessage: "Middle Name",
              })
        }
        placeholder={
          territory === TERRITORY.PAKISTAN
            ? "Father / Husband's Name"
            : "Middle Name"
        }
        required={territory === TERRITORY.KENYA}
      />
      <TextField
        fullWidth
        name={fieldNames.lastName}
        label={formatMessage({
          id: "patient.generalSection.lastName.label",
          defaultMessage: "Last Name",
        })}
        placeholder="Last Name"
        required
      />
      <DatePicker
        fullWidth
        disableFuture
        name={fieldNames.dob}
        label={formatMessage({
          id: "patient.generalSection.dob.label",
          defaultMessage: "Date of Birth",
        })}
        placeholder="Date of Birth"
        required
      />
      <GenderRadioInput
        fullWidth
        excludeTransgender
        name={fieldNames.gender}
        label={formatMessage({
          id: "patient.generalSection.sex.label",
          defaultMessage: "Sex",
        })}
        required
      />

      {!isEdit ? (
        <RadioGroupField
          name={fieldNames.newToFacility}
          label={formatMessage({
            id: "patient.generalSection.newToFacility.label",
            defaultMessage: "New to clinic?",
          })}
          data-testid="generalSection.newToFacility"
          required
          options={[
            { label: "Yes", value: true },
            { label: "No", value: false },
          ]}
        />
      ) : (
        <input
          hidden
          name={fieldNames.newToFacility}
          defaultValue={false as unknown as string}
          data-testid="generalSection.newToFacilityHidden"
        />
      )}
    </FormSection>
  );
};
